import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useShiftPatterns,
  useShiftPattern,
  useDeactivateShiftPattern,
  type ShiftPatternListItem,
} from '@/hooks/useShifts';
import { ShiftPatternFormDialog } from './ShiftPatternFormDialog';
import { useAuthStore } from '@/stores/authStore';

export function ShiftPatternsTab() {
  const { t } = useTranslation('shifts');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Pattern mutations require shifts.manage server-side; hide what would 403.
  const canManageShifts = hasPermission('shifts.manage');
  const { data: patterns = [], isLoading } = useShiftPatterns();
  const deactivatePattern = useDeactivateShiftPattern();

  const [formOpen, setFormOpen] = useState(false);
  const [editPatternId, setEditPatternId] = useState<string | null>(null);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);

  // Load full pattern data when editing
  const { data: editPattern } = useShiftPattern(editPatternId ?? undefined);

  const handleEdit = (id: string) => {
    setEditPatternId(id);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditPatternId(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditPatternId(null);
  };

  const handleDeactivate = async () => {
    if (!confirmDeactivateId) return;
    try {
      await deactivatePattern.mutateAsync(confirmDeactivateId);
      notify.success(t('patternDeactivated'));
      setConfirmDeactivateId(null);
    } catch {
      notify.error(tc('error'), t('errorSaving'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{tc('loading')}</Typography>
      </Box>
    );
  }

  const activePatterns = patterns.filter((p) => p.isActive);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">{t('patterns')}</Typography>
        {canManageShifts && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            {t('createPattern')}
          </Button>
        )}
      </Stack>

      {activePatterns.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('noPatterns')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('noPatternsDesc')}
          </Typography>
          {canManageShifts && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              {t('createPattern')}
            </Button>
          )}
        </Box>
      ) : (
        <Stack spacing={2}>
          {activePatterns.map((pattern) => (
            <PatternCard
              key={pattern.id}
              pattern={pattern}
              canManage={canManageShifts}
              onEdit={() => handleEdit(pattern.id)}
              onDeactivate={() => setConfirmDeactivateId(pattern.id)}
              t={t}
            />
          ))}
        </Stack>
      )}

      <ShiftPatternFormDialog
        open={formOpen}
        onClose={handleCloseForm}
        pattern={editPatternId ? editPattern : null}
      />

      <ConfirmDialog
        open={!!confirmDeactivateId}
        onCancel={() => setConfirmDeactivateId(null)}
        onConfirm={handleDeactivate}
        title={t('deactivatePattern')}
        message={t('deactivateConfirm')}
      />
    </Box>
  );
}

function PatternCard({
  pattern,
  canManage,
  onEdit,
  onDeactivate,
  t,
}: {
  pattern: ShiftPatternListItem;
  canManage: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  t: (key: string) => string;
}) {
  const { t: tc } = useTranslation('common');
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {pattern.name}
            </Typography>
            {pattern.description && (
              <Typography variant="body2" color="text.secondary">
                {pattern.description}
              </Typography>
            )}
          </Box>
          <Chip
            label={pattern.rotationType === 'fixed' ? t('fixed') : t('weeklyRotation')}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Stack>
        {pattern.rotationType === 'weekly_rotation' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('rotationPeriod')}: {pattern.rotationPeriodWeeks}
          </Typography>
        )}
      </CardContent>
      {canManage && (
        <CardActions>
          <Tooltip title={tc('edit')}>
            <IconButton size="small" onClick={onEdit} aria-label={tc('edit')}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('deactivatePattern')}>
            <IconButton size="small" onClick={onDeactivate} color="error" aria-label={t('deactivatePattern')}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      )}
    </Card>
  );
}
